import { useState } from "react";
import { useSeo } from "../lib/seo";
import {
  Building2,
  CheckCircle2,
  Clock,
  Globe,
  Handshake,
  LifeBuoy,
  Mail,
  MapPin,
  MessageSquare,
  Send,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Field, Input, Textarea } from "../components/ui/Input";
import { Reveal } from "../components/Reveal";

const TOPICS = [
  { icon: MessageSquare, label: "Product & pilots", note: "See it on your own systems" },
  { icon: LifeBuoy, label: "Support", note: "Help with your workspace" },
  { icon: Handshake, label: "Partnerships", note: "Build or resell with us" },
  { icon: ShieldCheck, label: "Security", note: "Reviews & disclosures" },
];

const REASONS = ["Product & pilots", "Support", "Partnerships", "Security", "Something else"];

export default function Contact() {
  useSeo({
    title: 'Contact Shineywise',
    description: 'Get in touch with Shineywise Technologies. Email us or visit our office in Noida, and our team will help with Wise 2.0 and enterprise integration.',
    path: '/contact',
  });
  const [form, setForm] = useState({ name: "", email: "", company: "", reason: REASONS[0], message: "" });
  const [sent, setSent] = useState(false);

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const subject = `[${form.reason}] Contact from ${form.name || "website"}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.company ? `Company: ${form.company}` : null,
      `Topic: ${form.reason}`,
      "",
      form.message,
    ]
      .filter((l) => l !== null)
      .join("\n");
    window.location.href = `mailto:info@shineywise.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-24">
        <div className="pointer-events-none absolute inset-0 bg-aurora opacity-60" />
        <div className="pointer-events-none absolute -left-24 -top-16 h-72 w-72 rounded-full bg-accent-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <Badge tone="violet" className="mx-auto border border-white/10 bg-white/10 text-accent-200">
              <Mail className="h-3.5 w-3.5" /> Contact
            </Badge>
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
              Let's talk.
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              A pilot, a partnership, a security question, or a hand with your workspace — tell us what
              you need and we'll get back to you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Topics */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TOPICS.map(({ icon: Icon, label, note }, i) => (
              <Reveal key={label} delay={i * 70}>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500">{note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact info + form */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          {/* Info */}
          <Reveal>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Get in touch</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                The fastest way to reach us is email — we read every message.
              </p>

              <div className="mt-8 space-y-4">
                <a href="mailto:info@shineywise.com" className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-card">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-glow">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Email us</p>
                    <p className="mt-0.5 text-sm text-brand-700">info@shineywise.com</p>
                  </div>
                </a>

                <a href="https://shineywise.com" target="_blank" rel="noreferrer" className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-card">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                    <Globe className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Website</p>
                    <p className="mt-0.5 text-sm text-slate-600">shineywise.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Visit us</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-600">
                      19th Floor, Tower C, Bhutani Alphathum,<br />
                      Sector 90, Noida, Uttar Pradesh 201304
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Response time</p>
                    <p className="mt-0.5 text-sm text-slate-600">We usually reply within 1–2 business days.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Building2 className="h-3.5 w-3.5" /> Operating entities
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
                  <li>Shineywise Technologies OPC Pvt. Ltd.</li>
                  <li>Shineywise Technologies LLC</li>
                </ul>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-card sm:p-8">
              {sent ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100">
                    <CheckCircle2 className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold text-slate-900">Your email is ready to send</h3>
                  <p className="mt-2 max-w-sm text-sm text-slate-500">
                    We opened your email app with the message pre-filled. If nothing happened, write to us
                    directly at{" "}
                    <a href="mailto:info@shineywise.com" className="font-medium text-brand-700 hover:underline">info@shineywise.com</a>.
                  </p>
                  <Button variant="outline" className="mt-6" onClick={() => setSent(false)}>
                    Edit message
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Name">
                      <Input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
                    </Field>
                    <Field label="Work email">
                      <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" />
                    </Field>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Company" hint="Optional">
                      <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Inc." />
                    </Field>
                    <Field label="Topic">
                      <select
                        value={form.reason}
                        onChange={(e) => set("reason", e.target.value)}
                        className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-soft transition-colors focus:border-accent-400 focus-ring"
                      >
                        {REASONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Message">
                    <Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us what you're trying to connect, or how we can help…" />
                  </Field>
                  <div className="flex items-center justify-between gap-4 pt-1">
                    <p className="text-xs text-slate-400">Opens in your email app · we never share your details.</p>
                    <Button type="submit">
                      <Send className="h-4 w-4" /> Send message
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
